import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { PrefixUrlPipe } from '../../pipes/prefix-url.pipe';
import {
  EVENTQ_REQUIRED_INVALID_MESSAGE,
  isEventqRequired,
} from '../../validators/required.validator';
import {
  computeSelectMenuStyle,
  EventqSelectMenuPlacement,
  EventqSelectMenuStyle,
  EventqSelectMenuWidth,
  EventqSelectOption,
  EventqSelectOptionGroup,
} from './select.types';

export type {
  EventqSelectMenuPlacement,
  EventqSelectMenuWidth,
  EventqSelectOption,
  EventqSelectOptionGroup,
} from './select.types';
export { computeSelectMenuStyle } from './select.types';

@Component({
  selector: 'eventq-select',
  imports: [MatIcon, PrefixUrlPipe],
  templateUrl: './select.html',
  styleUrl: './select.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.eventq-select-host--compact]': "density() === 'compact'",
    '[class.eventq-select-host--multiple]': 'multiple()',
    '[class.eventq-select-host--bare]': 'bareTrigger()',
    '[class.eventq-select-host--labeled]': '!!label()',
    '[class.eventq-select-host--danger]': "tone() === 'danger'",
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class EventqSelect {
  private readonly destroyRef = inject(DestroyRef);
  private readonly trigger = viewChild<ElementRef<HTMLElement>>('trigger');
  private readonly menu = viewChild<ElementRef<HTMLUListElement>>('menu');
  private menuHome: { parent: Node; next: ChildNode | null } | null = null;
  /** Menu node while it is attached to `document.body`. The host destroy does not remove it. */
  private portaledMenu: HTMLUListElement | null = null;

  readonly options = input<readonly EventqSelectOption[]>([]);
  /** When non-empty, menus render as priority-colored bands instead of a flat list. */
  readonly groups = input<readonly EventqSelectOptionGroup[]>([]);
  readonly value = input<string | null>(null);
  /** Selected ids when `multiple` is true. */
  readonly values = input<readonly string[]>([]);
  readonly multiple = input(false);
  readonly placeholder = input('Select');
  /** Floating notch label rendered on the trigger (Resolve-style fields). */
  readonly label = input('');
  /** Renders a red required asterisk after the floating label. */
  readonly requiredIndicator = input(false);
  /** When true (or when `requiredIndicator` is set), empty values fail validation. */
  readonly validateRequired = input<boolean | undefined>(undefined);
  readonly submitAttempted = input(false);
  readonly labelledBy = input<string | null>(null);
  readonly disabled = input(false);
  /** Force the invalid outline without a submit attempt. */
  readonly invalid = input(false);
  /** Filled trigger treatment. `danger` is the Skip outcome chrome (red-950). */
  readonly tone = input<'default' | 'danger'>('default');
  /** Compact 24px trigger for dense chrome bars (e.g. Caliber selection strip). */
  readonly density = input<'default' | 'compact'>('default');
  /**
   * Menu placement relative to the trigger.
   * The menu is always portaled to `document.body` so it escapes overflow clipping.
   * An ancestor scroll closes it so the list does not stay painted after the trigger moves.
   * An outside click closes it during capture, before that click can destroy the trigger.
   */
  readonly menuPlacement = input<EventqSelectMenuPlacement>('bottom-start');
  /** Match trigger width (default) or grow to option content. */
  readonly menuWidth = input<EventqSelectMenuWidth>('trigger');
  /** Optional 16px leading icon on the trigger (speed control). */
  readonly leadingIconSrc = input<string | null>(null);
  /** Hide the chevron for Figma chrome triggers (speed / AI Events). */
  readonly hideChevron = input(false);
  /** Transparent, padding-light trigger used as a label button. */
  readonly bareTrigger = input(false);
  /**
   * When set, the trigger shows this fixed label instead of the selected value
   * (AI Events chrome). Pair with `badgeCount`.
   */
  readonly triggerLabel = input<string | null>(null);
  /** Optional count pill after the trigger label. */
  readonly badgeCount = input<number | null>(null);
  /** When true, the trigger is a text field that filters options as the user types. */
  readonly searchable = input(false);

  readonly valueChange = output<string>();
  readonly valuesChange = output<string[]>();
  readonly validChange = output<boolean>();

  readonly menuOpen = signal(false);
  readonly menuStyle = signal<EventqSelectMenuStyle | null>(null);
  /** Filter query while the searchable menu is open. */
  readonly searchQuery = signal('');
  /** Visible text in the searchable trigger input. */
  readonly draftText = signal('');
  /** Keyboard highlight index into `navigableOptions` (-1 = none). */
  readonly activeOptionIndex = signal(-1);

  readonly resolvedOptions = computed(() => {
    const grouped = this.groups();
    if (grouped.length > 0) {
      return grouped.flatMap((group) => group.options);
    }
    return this.options();
  });

  readonly filteredOptions = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const opts = this.options();
    if (!query) {
      return opts;
    }
    return opts.filter((option) => option.label.toLowerCase().includes(query));
  });

  readonly filteredGroups = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const grouped = this.groups();
    if (!query) {
      return grouped;
    }
    return grouped
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => option.label.toLowerCase().includes(query)),
      }))
      .filter((group) => group.options.length > 0);
  });

  /** Flat option list used for ↑/↓ navigation (grouped or flat menus). */
  readonly navigableOptions = computed(() => {
    if (this.groups().length > 0) {
      return this.filteredGroups().flatMap((group) => group.options);
    }
    return this.filteredOptions();
  });

  readonly showNoMatches = computed(
    () => this.menuOpen() && this.navigableOptions().length === 0,
  );

  readonly selectedLabel = computed(() => {
    const forced = this.triggerLabel();
    if (forced) {
      return forced;
    }

    const opts = this.resolvedOptions();
    if (this.multiple()) {
      const selected = this.values();
      if (!selected.length) {
        return null;
      }
      if (selected.length === 1) {
        return opts.find((option) => option.id === selected[0])?.label ?? selected[0];
      }
      const first = opts.find((option) => option.id === selected[0])?.label ?? selected[0];
      return `${first} +${selected.length - 1}`;
    }

    const id = this.value();
    if (!id) {
      return null;
    }
    return opts.find((option) => option.id === id)?.label ?? null;
  });

  constructor() {
    const onScroll = (event: Event): void => this.onAncestorScroll(event);
    const onDocumentClick = (event: MouseEvent): void => this.onDocumentClick(event);
    document.addEventListener('scroll', onScroll, true);
    // Capture runs before the click target can destroy this view (section tab change).
    document.addEventListener('click', onDocumentClick, true);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('click', onDocumentClick, true);
      this.removePortaledMenu();
    });

    effect(() => {
      if (!this.menuOpen()) {
        return;
      }

      const menuEl = this.menu()?.nativeElement;
      const triggerEl = this.trigger()?.nativeElement;
      if (!menuEl || !triggerEl) {
        return;
      }

      this.portalMenu(menuEl);
      this.positionMenu(menuEl, triggerEl);
    });

    effect(() => {
      if (!this.searchable() || this.menuOpen()) {
        return;
      }
      this.draftText.set(this.selectedLabel() ?? '');
    });
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.eventq-select__root, .eventq-select__menu')) {
      return;
    }
    this.closeMenu();
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    if (!this.menuOpen()) {
      return;
    }
    const menuEl = this.menu()?.nativeElement;
    const triggerEl = this.trigger()?.nativeElement;
    if (!menuEl || !triggerEl) {
      return;
    }
    this.positionMenu(menuEl, triggerEl);
  }

  isSelected(optionId: string): boolean {
    if (this.multiple()) {
      return this.values().includes(optionId);
    }
    return optionId === this.value();
  }

  isActiveOption(optionId: string): boolean {
    const index = this.activeOptionIndex();
    if (index < 0) {
      return false;
    }
    return this.navigableOptions()[index]?.id === optionId;
  }

  requiredValidationEnabled(): boolean {
    const explicit = this.validateRequired();
    return explicit ?? this.requiredIndicator();
  }

  showValidationError(): boolean {
    return this.invalid() || (this.submitAttempted() && !this.isValid());
  }

  validationMessage(): string {
    if (this.requiredValidationEnabled() && !this.hasRequiredValue()) {
      return EVENTQ_REQUIRED_INVALID_MESSAGE;
    }
    return '';
  }

  isValid(): boolean {
    if (this.requiredValidationEnabled() && !this.hasRequiredValue()) {
      return false;
    }
    return true;
  }

  toggleMenu(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled()) {
      return;
    }
    if (this.menuOpen()) {
      this.closeMenu();
      return;
    }
    this.menuOpen.set(true);
  }

  onSelect(optionId: string, event?: Event): void {
    event?.stopPropagation();
    if (this.multiple()) {
      const next = this.values().includes(optionId)
        ? this.values().filter((id) => id !== optionId)
        : [...this.values(), optionId];
      this.valuesChange.emit(next);
      this.validChange.emit(next.length > 0 || !this.requiredValidationEnabled());
      return;
    }
    const label =
      this.resolvedOptions().find((option) => option.id === optionId)?.label ?? optionId;
    if (this.searchable()) {
      this.draftText.set(label);
    }
    this.valueChange.emit(optionId);
    this.validChange.emit(isEventqRequired(optionId) || !this.requiredValidationEnabled());
    this.closeMenu({ preserveDraft: this.searchable() });
  }

  private hasRequiredValue(): boolean {
    if (this.multiple()) {
      return this.values().length > 0;
    }
    return isEventqRequired(this.value() ?? '');
  }

  /**
   * Nested scrollers (the incident-report protocol stack) do not emit `window:scroll`.
   * Capture-phase scroll sees those, and the menu's own overflow does not close it.
   */
  private onAncestorScroll(event: Event): void {
    if (!this.menuOpen()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    const menuEl = this.menu()?.nativeElement;
    if (menuEl && (target === menuEl || menuEl.contains(target))) {
      return;
    }
    const triggerEl = this.trigger()?.nativeElement;
    if (!triggerEl || !target.contains(triggerEl)) {
      return;
    }
    this.closeMenu();
  }

  /** Closes an open menu without changing the selection. */
  dismiss(): void {
    if (this.menuOpen()) {
      this.closeMenu();
    }
  }

  private closeMenu(options?: { preserveDraft?: boolean }): void {
    this.restoreMenu();
    this.menuOpen.set(false);
    this.menuStyle.set(null);
    this.searchQuery.set('');
    this.activeOptionIndex.set(-1);
    if (this.searchable() && !options?.preserveDraft) {
      this.draftText.set(this.selectedLabel() ?? '');
    }
  }

  onTriggerFocus(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled() || this.menuOpen()) {
      return;
    }
    this.menuOpen.set(true);
  }

  onTriggerInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const text = target?.value ?? '';
    this.draftText.set(text);
    this.searchQuery.set(text);
    this.activeOptionIndex.set(-1);
    if (!this.menuOpen()) {
      this.menuOpen.set(true);
    }
    if (!text.trim()) {
      this.valueChange.emit('');
      this.validChange.emit(!this.requiredValidationEnabled());
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (!this.searchable() || this.disabled()) {
      return;
    }

    const key = event.key;
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      if (!this.menuOpen()) {
        this.menuOpen.set(true);
      }
      const options = this.navigableOptions();
      if (!options.length) {
        return;
      }
      const last = options.length - 1;
      const current = this.activeOptionIndex();
      if (key === 'ArrowDown') {
        if (current < 0) {
          this.activeOptionIndex.set(0);
        } else {
          this.activeOptionIndex.set(Math.min(current + 1, last));
        }
      } else if (current < 0) {
        this.activeOptionIndex.set(last);
      } else {
        this.activeOptionIndex.set(Math.max(current - 1, 0));
      }
      return;
    }

    if (key === 'Enter') {
      const index = this.activeOptionIndex();
      const option = index >= 0 ? this.navigableOptions()[index] : undefined;
      if (!option) {
        return;
      }
      event.preventDefault();
      this.onSelect(option.id, event);
      return;
    }

    if (key === 'Escape' && this.menuOpen()) {
      event.preventDefault();
      this.closeMenu();
    }
  }

  private portalMenu(menu: HTMLUListElement): void {
    this.portaledMenu = menu;
    if (menu.parentElement === document.body) {
      return;
    }
    this.menuHome = {
      parent: menu.parentNode as Node,
      next: menu.nextSibling,
    };
    document.body.appendChild(menu);
  }

  private restoreMenu(): void {
    const menu = this.menu()?.nativeElement ?? this.portaledMenu;
    if (!menu || !this.menuHome) {
      this.menuHome = null;
      this.portaledMenu = null;
      return;
    }

    if (this.menuHome.next?.parentNode === this.menuHome.parent) {
      this.menuHome.parent.insertBefore(menu, this.menuHome.next);
    } else {
      this.menuHome.parent.appendChild(menu);
    }
    this.menuHome = null;
    this.portaledMenu = null;
  }

  /** Drops the menu this instance attached to `document.body`. Other selects are left alone. */
  private removePortaledMenu(): void {
    const menu = this.portaledMenu;
    this.portaledMenu = null;
    this.menuHome = null;
    if (menu?.parentElement === document.body) {
      menu.remove();
    }
  }

  private positionMenu(menu: HTMLUListElement, trigger: HTMLElement): void {
    const triggerRect = trigger.getBoundingClientRect();
    const widthMode = this.menuWidth();
    const style = computeSelectMenuStyle(triggerRect, this.menuPlacement(), 4, widthMode);
    const groupsTransparent = this.groups().length > 0;
    this.menuStyle.set(style);

    menu.style.position = 'fixed';
    menu.style.zIndex = '1200';
    menu.style.top = style.top;
    menu.style.bottom = style.bottom;
    menu.style.left = style.left;
    menu.style.width = style.width;
    menu.style.minWidth = widthMode === 'content' ? '7.5rem' : style.width;
    menu.style.right =
      style.left === 'auto' ? `${window.innerWidth - triggerRect.right}px` : 'auto';
    menu.style.boxSizing = 'border-box';
    menu.style.margin = '0';
    menu.style.padding = groupsTransparent ? '0' : '4px';
    menu.style.listStyle = 'none';
    menu.style.maxHeight = 'min(280px, calc(100vh - 24px))';
    menu.style.overflowX = 'hidden';
    menu.style.overflowY = 'auto';
    menu.style.background = groupsTransparent
      ? 'transparent'
      : 'var(--eventq-color-hub-bg, #1e1e2d)';
    menu.style.boxShadow = '0 4px 12px rgb(0 0 0 / 35%)';
  }
}
